import { Money, StruckSnapshot } from "@redotech/money";
import { Currency, isCurrency } from "@redotech/money/currencies";

/**
 * Adapt a display amount (number or numeric string) plus a currency code into a
 * {@link Money} for `<MoneyText>`. The currency is honored when valid and falls
 * back to USD otherwise — carrier shipping rates are quoted in USD and often
 * omit a currency, while order amounts carry their real presentment currency.
 *
 * Pass `struck` to attach a frozen store-currency snapshot (e.g. a purchased
 * label's rate): `MoneyText` then renders that frozen figure instead of a live
 * conversion.
 *
 * Returns `null` for absent/non-finite amounts so call sites can pass optional
 * data straight through; `MoneyText` renders nothing for a nullish value.
 */
export function toMoney(
  input:
    | {
        amount: string | number | null | undefined;
        currency?: string | null;
        struck?: StruckSnapshot | null;
      }
    | null
    | undefined,
): Money | null {
  if (!input || input.amount == null) {
    return null;
  }
  const amount = Number(input.amount);
  if (!Number.isFinite(amount)) {
    return null;
  }
  const currency =
    input.currency && isCurrency(input.currency)
      ? input.currency
      : Currency.USD;
  return input.struck
    ? { amount, currency, struck: input.struck }
    : { amount, currency };
}
